import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronDown, ChevronRight, BookOpen, Settings } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Traffic light helpers ─────────────────────────────────────────────────────
const STATUS_CYCLE = [null, 'red', 'yellow', 'green'];
const STATUS_LABELS = { green: 'Придобита', yellow: 'В процес', red: 'Не придобита', null: 'Не е оценена' };
const STATUS_BG = {
  green:  'bg-green-500 hover:bg-green-600 text-white',
  yellow: 'bg-yellow-400 hover:bg-yellow-500 text-white',
  red:    'bg-red-500 hover:bg-red-600 text-white',
  null:   'bg-slate-200 hover:bg-slate-300 text-slate-500',
};
const STATUS_DOT = {
  green:  'bg-green-500',
  yellow: 'bg-yellow-400',
  red:    'bg-red-500',
  null:   'bg-slate-300',
};

function nextStatus(current) {
  const idx = STATUS_CYCLE.indexOf(current ?? null);
  return STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function CompetenciesPage({ classes, token }) {
  const [view, setView] = useState('assess'); // 'assess' | 'manage'

  // Assess state
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState({}); // { student_id+item_id: status }
  const [saving, setSaving] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  // Manage state
  const [manageClassId, setManageClassId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupSubject, setNewGroupSubject] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [newItemText, setNewItemText] = useState({});
  const [newItemSub, setNewItemSub] = useState({});
  const [submittingGroup, setSubmittingGroup] = useState(false);
  const [manageGroups, setManageGroups] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch competency groups for the selected class
  const fetchGroups = useCallback(async () => {
    if (!selectedClass) { setGroups([]); return; }
    try {
      const res = await axios.get(`${API_URL}/competency-groups`, { 
        headers,
        params: { class_id: selectedClass }
      });
      setGroups(res.data);
    } catch { toast.error('Грешка при зареждане на компетентностите'); }
  }, [selectedClass, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Fetch students when class changes
  useEffect(() => {
    if (!selectedClass) { setStudents([]); return; }
    axios.get(`${API_URL}/classes/${selectedClass}/students`, { headers })
      .then(res => setStudents(res.data))
      .catch(() => toast.error('Грешка при зареждане на ученици'));
  }, [selectedClass]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch assignments when class+group changes
  useEffect(() => {
    if (!selectedClass || !selectedGroup) { setAssignments({}); return; }
    setLoadingData(true);
    axios.get(`${API_URL}/competencies/class/${selectedClass}`, {
      headers,
      params: { group_id: selectedGroup }
    })
      .then(res => {
        const map = {};
        res.data.forEach(a => { map[`${a.student_id}__${a.item_id}`] = a.status; });
        setAssignments(map);
      })
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, [selectedClass, selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCellClick = async (studentId, item, currentStatus) => {
    const newSt = nextStatus(currentStatus ?? null);
    const key = `${studentId}__${item.item_id}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    // Optimistic update
    setAssignments(prev => ({ ...prev, [key]: newSt }));
    try {
      await axios.post(`${API_URL}/competencies/assign`, {
        student_id: studentId,
        item_id: item.item_id,
        group_id: item.group_id,
        class_id: selectedClass,
        status: newSt,
      }, { headers });
    } catch {
      // Revert on error
      setAssignments(prev => ({ ...prev, [key]: currentStatus }));
      toast.error('Грешка при запазване');
    } finally {
      setSaving(prev => { const n = {...prev}; delete n[key]; return n; });
    }
  };

  // Group items by sub_section
  const groupItemsBySub = (items) => {
    const sections = [];
    const seen = new Set();
    items.forEach(it => {
      const sec = it.sub_section ?? '__none__';
      if (!seen.has(sec)) { seen.add(sec); sections.push(sec); }
    });
    return sections.map(sec => ({
      label: sec === '__none__' ? null : sec,
      items: items.filter(it => (it.sub_section ?? '__none__') === sec)
    }));
  };

  const currentGroup = groups.find(g => g.group_id === selectedGroup);
  const sections = currentGroup ? groupItemsBySub(currentGroup.items) : [];

  // ── Fetch manage groups when manageClassId changes ────────────────────────────
  useEffect(() => {
    if (!manageClassId) { setManageGroups([]); return; }
    axios.get(`${API_URL}/competency-groups`, { 
      headers,
      params: { class_id: manageClassId }
    })
      .then(res => setManageGroups(res.data))
      .catch(() => toast.error('Грешка при зареждане'));
  }, [manageClassId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Manage handlers ──────────────────────────────────────────────────────────
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim() || !newGroupSubject.trim() || !manageClassId) return;
    setSubmittingGroup(true);
    try {
      const res = await axios.post(`${API_URL}/competency-groups`,
        { name: newGroupName.trim(), subject: newGroupSubject.trim(), class_id: manageClassId }, { headers });
      setManageGroups(prev => [...prev, res.data]);
      setNewGroupName(''); setNewGroupSubject('');
      toast.success('Групата е създадена');
    } catch { toast.error('Грешка'); }
    finally { setSubmittingGroup(false); }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Изтриване на цялата група с всичките й компетентности?')) return;
    try {
      await axios.delete(`${API_URL}/competency-groups/${groupId}`, { headers });
      setManageGroups(prev => prev.filter(g => g.group_id !== groupId));
      // Also remove from assess groups if same class
      if (selectedClass === manageClassId) {
        setGroups(prev => prev.filter(g => g.group_id !== groupId));
      }
      toast.success('Групата е изтрита');
    } catch (err) { toast.error(err.response?.data?.detail || 'Грешка'); }
  };

  const handleAddItem = async (groupId) => {
    const text = (newItemText[groupId] || '').trim();
    if (!text || !manageClassId) return;
    try {
      const res = await axios.post(`${API_URL}/competency-items`, {
        group_id: groupId,
        class_id: manageClassId,
        text,
        sub_section: (newItemSub[groupId] || '').trim() || null,
        order: (manageGroups.find(g => g.group_id === groupId)?.items.length || 0) + 1,
      }, { headers });
      setManageGroups(prev => prev.map(g =>
        g.group_id === groupId ? { ...g, items: [...g.items, res.data] } : g
      ));
      setNewItemText(prev => ({ ...prev, [groupId]: '' }));
      setNewItemSub(prev => ({ ...prev, [groupId]: '' }));
      toast.success('Компетенцията е добавена');
    } catch { toast.error('Грешка'); }
  };

  const handleDeleteItem = async (groupId, itemId) => {
    try {
      await axios.delete(`${API_URL}/competency-items/${itemId}`, { headers });
      setManageGroups(prev => prev.map(g =>
        g.group_id === groupId ? { ...g, items: g.items.filter(i => i.item_id !== itemId) } : g
      ));
      toast.success('Изтрита');
    } catch { toast.error('Грешка'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-slate-900">Компетентности</h2>
        <div className="flex gap-2">
          <Button
            data-testid="assess-view-btn"
            variant={view === 'assess' ? 'default' : 'outline'}
            onClick={() => setView('assess')}
            className={view === 'assess' ? 'bg-[#00966E] hover:bg-[#007a59] text-white' : ''}
          >
            <BookOpen className="h-4 w-4 mr-2" /> Оценяване
          </Button>
          <Button
            data-testid="manage-view-btn"
            variant={view === 'manage' ? 'default' : 'outline'}
            onClick={() => setView('manage')}
            className={view === 'manage' ? 'bg-[#00966E] hover:bg-[#007a59] text-white' : ''}
          >
            <Settings className="h-4 w-4 mr-2" /> Управление
          </Button>
        </div>
      </div>

      {/* ── ASSESS VIEW ───────────────────────────────────────────────────────── */}
      {view === 'assess' && (
        <div className="space-y-4">
          {/* Selectors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Клас</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="mt-1" data-testid="assess-class-select">
                  <SelectValue placeholder="Изберете клас..." />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Група компетентности</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="mt-1" data-testid="assess-group-select">
                  <SelectValue placeholder="Изберете група..." />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.group_id} value={g.group_id}>
                      {g.name} <span className="text-slate-400 text-xs ml-1">({g.subject})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Legend */}
          {selectedGroup && (
            <div className="flex gap-4 text-xs text-slate-500 flex-wrap items-center">
              <span className="font-medium text-slate-600">Легенда:</span>
              {['green','yellow','red',null].map(s => (
                <span key={String(s)} className="flex items-center gap-1">
                  <span className={`inline-block w-3 h-3 rounded-full ${STATUS_DOT[s]}`} />
                  {STATUS_LABELS[s]}
                </span>
              ))}
              <span className="flex items-center gap-1 ml-2 border-l pl-3">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-600 text-xs font-bold">!</span>
                <span>Ред с над 30% не придобити</span>
              </span>
              <span className="text-slate-400 italic">Задръжте върху #{`#`}N за пълен текст</span>
            </div>
          )}

          {loadingData && <p className="text-slate-500 text-sm">Зареждане...</p>}

          {!loadingData && selectedClass && selectedGroup && students.length === 0 && (
            <p className="text-slate-400 text-sm">Няма ученици в избрания клас.</p>
          )}

          {/* Matrix */}
          {!loadingData && students.length > 0 && currentGroup && (
            <div className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  {/* Sub-section headers */}
                  {sections.some(s => s.label) && (
                    <tr className="bg-slate-100">
                      <th className="border-b border-r px-3 py-2 text-left min-w-[160px] sticky left-0 bg-slate-100" />
                      {sections.map((sec, si) => (
                        <th
                          key={si}
                          colSpan={sec.items.length}
                          className="border-b border-r px-2 py-1.5 text-center text-xs font-semibold text-slate-600"
                        >
                          {sec.label || ''}
                        </th>
                      ))}
                    </tr>
                  )}
                  {/* Item headers */}
                  <tr className="bg-slate-50">
                    <th className="border-b border-r px-3 py-2 text-left font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[160px]">
                      Ученик
                    </th>
                    {sections.flatMap(sec => sec.items.map(item => (
                      <th
                        key={item.item_id}
                        className="border-b border-r px-1 py-2 text-center min-w-[44px] max-w-[44px]"
                      >
                        <div className="relative group flex items-center justify-center">
                          <span className="text-xs text-slate-500 font-medium cursor-help">
                            #{item.order}
                          </span>
                          {/* Styled tooltip with higher z-index */}
                          <div className="absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 z-[9999] w-56 bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none whitespace-normal text-left leading-relaxed">
                            {item.sub_section && (
                              <span className="block text-green-300 font-semibold mb-0.5">{item.sub_section}</span>
                            )}
                            {item.text}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                          </div>
                        </div>
                      </th>
                    )))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => {
                    // Row color coding: >30% red items → light red row
                    const allItems = sections.flatMap(sec => sec.items);
                    const redCount = allItems.filter(item =>
                      assignments[`${student.user_id}__${item.item_id}`] === 'red'
                    ).length;
                    const hasHighRed = allItems.length > 0 && (redCount / allItems.length) > 0.30;
                    const rowBg = hasHighRed ? '#fff1f2' : (idx % 2 === 0 ? '#fff' : '#f9fafb');

                    return (
                    <tr key={student.user_id} className={hasHighRed ? 'ring-1 ring-inset ring-red-200' : ''}>
                      <td
                        className="border-b border-r px-3 py-2 font-medium text-slate-800 sticky left-0 whitespace-nowrap"
                        style={{ background: rowBg }}
                      >
                        <div className="flex items-center gap-2">
                          {student.name}
                          {hasHighRed && (
                            <span
                              title={`${redCount} от ${allItems.length} компетентности не са придобити`}
                              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs font-bold shrink-0 cursor-help"
                            >
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      {sections.flatMap(sec => sec.items.map(item => {
                        const key = `${student.user_id}__${item.item_id}`;
                        const status = assignments[key] ?? null;
                        const isSaving = !!saving[key];
                        return (
                          <td key={item.item_id} className="border-b border-r px-1 py-1 text-center" style={{ background: rowBg }}>
                            <button
                              type="button"
                              data-testid={`cell-${student.user_id}-${item.item_id}`}
                              onClick={() => handleCellClick(student.user_id, item, status)}
                              disabled={isSaving}
                              title={`${student.name} — ${item.text}\n${STATUS_LABELS[status]}`}
                              className={`w-8 h-8 rounded-full transition-all text-xs font-bold ${STATUS_BG[status]} ${isSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                            >
                              {status === 'green' && '✓'}
                              {status === 'yellow' && '~'}
                              {status === 'red' && '✗'}
                              {!status && ''}
                            </button>
                          </td>
                        );
                      }))}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Competency legend list */}
          {currentGroup && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {currentGroup.items.map(item => (
                <div key={item.item_id} className="flex items-start gap-2 text-xs text-slate-600 py-0.5">
                  <span className="font-bold text-slate-400 shrink-0">#{item.order}</span>
                  <span>{item.sub_section && <span className="text-[#00966E] font-medium">[{item.sub_section}] </span>}{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MANAGE VIEW ───────────────────────────────────────────────────────── */}
      {view === 'manage' && (
        <div className="space-y-5">
          {/* Class selector for manage view */}
          <div className="max-w-md">
            <Label className="text-slate-700 font-medium">Изберете клас за управление на компетентности</Label>
            <Select value={manageClassId} onValueChange={setManageClassId}>
              <SelectTrigger className="mt-1" data-testid="manage-class-select">
                <SelectValue placeholder="Изберете клас..." />
              </SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!manageClassId && (
            <div className="text-center py-8 text-slate-400">
              <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Моля, изберете клас за да управлявате компетентностите му.</p>
            </div>
          )}

          {manageClassId && (
            <>
              {/* Add new group form */}
              <Card className="p-4 border-dashed border-2 border-slate-300">
                <h3 className="font-semibold text-slate-700 mb-3">Нова група компетентности</h3>
                <form onSubmit={handleCreateGroup} className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label>Наименование</Label>
                    <Input
                      value={newGroupName}
                      onChange={e => setNewGroupName(e.target.value)}
                      placeholder="напр. Математически компетентности"
                      data-testid="new-group-name-input"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="w-[140px]">
                    <Label>Предмет</Label>
                    <Input
                      value={newGroupSubject}
                      onChange={e => setNewGroupSubject(e.target.value)}
                      placeholder="напр. Математика"
                      data-testid="new-group-subject-input"
                      className="mt-1"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submittingGroup}
                    data-testid="create-group-btn"
                    className="bg-[#00966E] hover:bg-[#007a59] text-white"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Добави група
                  </Button>
                </form>
              </Card>

              {/* Groups list */}
              {manageGroups.length === 0 && (
                <div className="text-center py-6 text-slate-400">
                  <p>Все още няма създадени групи компетентности за този клас.</p>
                  <p className="text-sm mt-1">Използвайте формата по-горе за да добавите нова група.</p>
                </div>
              )}

              {manageGroups.map(group => (
                <Card key={group.group_id} className="border border-slate-200">
                  {/* Group header */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [group.group_id]: !prev[group.group_id] }))}
                        className="flex items-center gap-2 font-semibold text-slate-800 hover:text-[#00966E]"
                        data-testid={`expand-group-${group.group_id}`}
                      >
                        {expandedGroups[group.group_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {group.name}
                      </button>
                      <Badge className="text-xs bg-blue-100 text-blue-700 font-normal">{group.subject}</Badge>
                      <Badge className="text-xs bg-slate-100 text-slate-500 font-normal">{group.items.length} компетентности</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteGroup(group.group_id)}
                      data-testid={`delete-group-${group.group_id}`}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Items */}
                  {expandedGroups[group.group_id] && (
                    <div className="border-t px-4 pb-4">
                      <div className="divide-y mb-3">
                        {group.items.map(item => (
                          <div key={item.item_id} className="flex items-center justify-between py-2 gap-2">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="text-xs font-bold text-slate-400 shrink-0 mt-0.5">#{item.order}</span>
                              {item.sub_section && (
                                <Badge className="text-xs bg-green-100 text-green-700 font-normal shrink-0">{item.sub_section}</Badge>
                              )}
                              <span className="text-sm text-slate-700">{item.text}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteItem(group.group_id, item.item_id)}
                              className="text-red-400 hover:bg-red-50 shrink-0"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                        {group.items.length === 0 && (
                          <p className="py-3 text-sm text-slate-400 italic">Няма добавени компетентности.</p>
                        )}
                      </div>

                      {/* Add item form */}
                      <div className="flex gap-2 flex-wrap">
                        <Input
                          value={newItemSub[group.group_id] || ''}
                          onChange={e => setNewItemSub(prev => ({ ...prev, [group.group_id]: e.target.value }))}
                          placeholder="Подраздел (по желание)"
                          className="w-[160px] text-sm h-9"
                        />
                        <Input
                          value={newItemText[group.group_id] || ''}
                          onChange={e => setNewItemText(prev => ({ ...prev, [group.group_id]: e.target.value }))}
                          placeholder="Текст на компетенцията..."
                          data-testid={`new-item-input-${group.group_id}`}
                          className="flex-1 min-w-[200px] text-sm h-9"
                          onKeyDown={e => e.key === 'Enter' && handleAddItem(group.group_id)}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleAddItem(group.group_id)}
                          data-testid={`add-item-btn-${group.group_id}`}
                          className="bg-[#00966E] hover:bg-[#007a59] text-white h-9"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Добави
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
